import { ColorTypes } from '@pinchenterprisesnpm/friday-ui'
import React from 'react'
import store from '@src/store/store'
import { OpenModal, CloseModal } from 'data/Modals/actions'
import { TModalConfig } from 'models/Modals'

const noop = () => {}

export type TConfirmModalConfig = {
  onConfirm: () => void
  onCancel: () => void
  onClose: () => void
  title: string
  message: React.ReactNode
  height: number | string
  width: number | string
  shouldWaitPromise: boolean
  cancelButtonText: string
  confirmButtonText: string
  isLoading: boolean
  primaryButtonType?: ColorTypes
  primaryFilledOutlined?: boolean
  secondaryButtonType?: ColorTypes
  secondaryFilledOutlined?: boolean
  showConfirmButton?: boolean
  showCancelButton?: boolean
}

/**
 * Generic confirm modal
 *
 * @param config Config for the confirmation modal
 */
export const confirmModal = (config: Partial<TConfirmModalConfig> = {}) => {
  const {
    onConfirm = noop,
    onCancel = noop,
    onClose = noop,
    title = 'Attention required',
    message = "You will lose the changes you've made. Are you sure you want to continue?",
    width = 'unset',
    shouldWaitPromise = true,
    cancelButtonText = 'Cancel',
    confirmButtonText = 'Confirm',
    isLoading = false,
    primaryButtonType = 'primary',
    primaryFilledOutlined = false,
    secondaryButtonType = 'dark',
    secondaryFilledOutlined = true,
    showConfirmButton = true,
    showCancelButton = true,
  } = config

  const modalIdentifier = `confirm-modal-${Date.now()}`

  // Convert width to maxWidth breakpoint if it's a number
  const getMaxWidth = () => {
    if (typeof width === 'number') {
      if (width <= 400) return 'xs'
      if (width <= 600) return 'sm'
      if (width <= 900) return 'md'
      if (width <= 1200) return 'lg'
      return 'xl'
    }
    return 'xs' // default for string values
  }

  const modalConfig: TModalConfig = {
    identifier: modalIdentifier,
    title: title,
    content: message,
    onClose,
    onConfirm: () => {
      if (shouldWaitPromise) {
        const promise = (onConfirm as () => Promise<any>)()
        if (promise && promise.then) {
          promise.then(() => {
            store.dispatch(CloseModal(modalIdentifier))
          })
        } else {
          store.dispatch(CloseModal(modalIdentifier))
        }
      } else {
        onConfirm()
        store.dispatch(CloseModal(modalIdentifier))
      }
    },
    onCancel: () => {
      onCancel()
    },
    showCancelButton,
    showConfirmButton,
    cancelButtonLabel: cancelButtonText,
    confirmButtonLabel: confirmButtonText,
    cancelButtonType: secondaryButtonType,
    confirmButtonType: primaryButtonType,
    cancelFilledOutlined: secondaryFilledOutlined,
    confirmFilledOutlined: primaryFilledOutlined,
    isLoading,
    fullWidth: true,
    maxWidth: getMaxWidth(),
  }

  store.dispatch(OpenModal(modalConfig))
}
